import React, { memo, type MouseEvent, type KeyboardEvent, useRef, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import {
	withConnectHost,
	isConnectDialogWebItem,
} from '@atlassian/jira-connect-utils/src/common/utils/ecosystem-connect-host.tsx';
import type { ACJSHost } from '@atlassian/jira-connect/src/types.tsx';
import type { ConnectDataItem } from '@atlassian/jira-navigation-apps-sidebar-connect/src/common/types.tsx';
import AppItemIcon from '@atlassian/jira-horizontal-nav-icons/src/ui/app-item-icon/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { Link } from '@atlassian/react-resource-router';

type Props = {
	data: ConnectDataItem;
	onItemClick: (e: MouseEvent<Element> | KeyboardEvent<Element>) => void;
};

// Perform a BFS to find the first connect page that does not have children.
// This will be used as the default selected item when the app is clicked from the apps dropdown menu.
const getItemLink = (connectItem: ConnectDataItem) => {
	const queue = [connectItem];

	while (queue.length > 0) {
		const current = queue.shift();

		if (current != null && isEmpty(current.items)) {
			return current.url;
		}

		current?.items?.forEach((childItem) => {
			queue.push(childItem);
		});
	}

	return '';
};

const ConnectItem = ({ data, onItemClick }: Props) => {
	const webItemRef = useRef<HTMLElement | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(
		(e: MouseEvent<Element> | KeyboardEvent<Element>) => {
			withConnectHost((connectHost: ACJSHost) => {
				if (webItemRef.current && data.url) {
					connectHost.triggerWebItem(webItemRef.current, data.styleClass ?? '', data.url);
				}
			});
			if (data.styleClass && isConnectDialogWebItem(data.styleClass)) {
				e.preventDefault();
				e.stopPropagation();
				fireUIAnalytics(createAnalyticsEvent({}), 'link clicked', 'app', {
					appName: data.name,
					appType: 'connect',
				});
			} else {
				onItemClick(e);
			}
		},
		[data, onItemClick, createAnalyticsEvent],
	);

	return (
		<DropdownItem
			// @ts-expect-error - dropdown item is poorly typed and won't allow Link to be used even though it is compatible
			component={Link}
			href={getItemLink(data)}
			elemBefore={<AppItemIcon url={data.iconUrl} />}
			onClick={handleClick}
			UNSAFE_shouldDisableRouterLink
			ref={(ref) => {
				if (ref) {
					webItemRef.current = ref;
				}
			}}
		>
			{data.name}
		</DropdownItem>
	);
};

export default memo<Props>(ConnectItem);
